/**
 * The app's one way to show a message: the snackbar shown after login
 * (context/SnackbarContext + components/ui/Snackbar).
 *
 * Components can call useSnackbar(). Code that cannot use the hook (auth
 * providers above <SnackbarProvider>, plain helpers) and screens written for
 * react-hot-toast use this module, which keeps that call style:
 *
 *   toast.success('Saved');  toast.error('Failed');  toast.info('Note');  toast('Note');
 *   toast.loading('Exporting…', { id: 'export' });  // later replaced by
 *   toast.success('Export downloaded', { id: 'export' });
 */
let snackbar = null; // { showSnackbar, removeSnackbar } from SnackbarProvider
const queued = []; // messages sent before the provider mounted
const shownById = new Map(); // caller's id -> snackbar id, so a later message replaces it

const LOADING_DURATION = 60000; // until the follow-up message replaces it

const textOf = (message) => {
  if (typeof message === 'string') return message;
  if (message instanceof Error) return message.message;
  return message?.message || String(message ?? '');
};

function show(message, variant, options = {}) {
  if (!snackbar) {
    queued.push([message, variant, options]);
    return options.id;
  }
  if (options.id && shownById.has(options.id)) {
    snackbar.removeSnackbar(shownById.get(options.id));
  }
  const id = snackbar.showSnackbar(
    textOf(message),
    variant,
    options.duration ? { duration: options.duration } : {},
  );
  if (options.id) shownById.set(options.id, id);
  return options.id || id;
}

/** Called by SnackbarProvider on mount; returns the disconnect function. */
export function connectSnackbar(api) {
  snackbar = api;
  queued.splice(0).forEach(([message, variant, options]) => show(message, variant, options));
  return () => {
    if (snackbar === api) snackbar = null;
  };
}

const toast = (message, options) => show(message, 'info', options);
toast.success = (message, options) => show(message, 'success', options);
toast.error = (message, options) => show(message, 'error', options);
toast.info = (message, options) => show(message, 'info', options);
toast.warning = (message, options) => show(message, 'warning', options);
toast.loading = (message, options = {}) => show(message, 'info', { duration: LOADING_DURATION, ...options });
toast.dismiss = (id) => {
  if (!snackbar || !id) return;
  snackbar.removeSnackbar(shownById.get(id) || id);
  shownById.delete(id);
};

export { toast };
export default toast;
